import { Box, Button, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, TableRow, Typography } from "@mui/material"
import { useFormContext } from "react-hook-form"
import DialogAppBar from "../../../../../components/DialogAppBar"
import useMobileScreen from "../../../../../lib/useMobileScreen";
import AddIcon from '@mui/icons-material/Add';
import DisplayNotFoundInfo from "../../../../../components/DisplayNotFindInfo";
import EditIcon from '@mui/icons-material/Edit';
import { useContext, useState } from "react";
import { ConfigLimiteCred } from ".";
import DialogConfiguracao from "./DialogConfiguracao";
import axiosClient from "../../../../../lib/axios";
import { useQuery } from "react-query";
import LoadingApiQuery from "../../../../../components/LoadingApiQuery";
import { formatarValorMonetario } from "../../../../../helpers/masks";
import moment from "moment";
import { IDataDetalharConfigLimiteCred } from "../../../../../types/api/geral/detalhar-configuracoes-limite-credito";
import DisplayDadosClienteFormulario from "../DisplayDadosClienteFormulario";
import { IDataPublicos } from "../../../../../types/api/geral/pesquisar-publicos";
import { AuthContext } from "../../../../../contexts/AuthContext";
import { enqueueSnackbar } from "notistack";

export default function Formulario({
    handleClose,
    handleBack
}: FormularioProps) {

    const telaMobile = useMobileScreen();
    const { getUserData } = useContext(AuthContext);

    const [modalConfig, setModalConfig] = useState<IModalConfig>({
        state: false,
        index: null,
        limite_alcada: 0
    })

    //formulario
    const { setValue, watch } = useFormContext()

    const publico: IDataPublicos = watch("cliente")
    const configsLimiteCredito = watch("listaAttLimiteCredito")
    const existeConfiguracoes = configsLimiteCredito.length > 0
    //

    //querys e mutations
    const { isLoading } = useQuery({
        queryKey: ['detalhar-configuracoes-limite-credito', publico],
        queryFn: () => axiosClient.get("/detalhar-configuracoes-limite-credito", {
            params: {
                codigo: publico.codigo_publico,
                tipo: publico.tipo_publico
            }
        }),
        onSuccess: ({ data }: { data: IDataDetalharConfigLimiteCred[] }) => {
            const params: ConfigLimiteCred[] = data.map((item) => ({
                tipo: item.tipo,
                limiteCredito: formatarValorMonetario(item.valor),
                vigencia: {
                    inicio: moment(item.vigencia_inicio),
                    fim: moment(item.vigencia_fim),
                },
                temContrato: item.possui_contrato,
                registrado: item.possui_registro,
                valorContrato: formatarValorMonetario(item.contrato_valor),
                vencimentoContrato: item.contrato_vencimento ? moment(item.contrato_vencimento) : null,
                garantias: item.garantias
            }))
            setValue("listaAttLimiteCredito", params)
        },
        cacheTime: 0
    });
    //

    //funçoes manipuladoras
    const handleOpenModalConfig = (index: number | null) => {
        const perfilAlcada = getUserData()?.perfis_vinculados.filter((perfil) => perfil.limite_alcada);
        if (!perfilAlcada || perfilAlcada.length === 0) {
            return enqueueSnackbar("Usuário não possui perfil com alçada definida", { variant: "error" });
        }
        setModalConfig({ state: true, index, limite_alcada: perfilAlcada[0].limite_alcada ?? 0 })
    }
    const handleCloseModalConfig = () => setModalConfig({ state: false, index: null, limite_alcada: 0 });
    //

    return (
        <>
            {
                telaMobile &&
                <DialogAppBar
                    {...{ handleClose }}
                    title="Solicitar análise de crédito"
                />
            }

            <DialogTitle id="draggable-dialog-title">
                Detalhe do público
            </DialogTitle>

            <DialogContent sx={{ mt: { xs: 2, md: 0 } }}>
                <Box
                    p={2}
                    bgcolor="#e5f6fd"
                    borderRadius={0.5}
                >
                    <DisplayDadosClienteFormulario tipoDado="context" />
                </Box>

                {isLoading && <LoadingApiQuery />}

                {
                    !isLoading &&
                    <>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mt={3}>
                            <Typography variant="h6" >Configurações de limite de crédito</Typography>

                            {
                                !existeConfiguracoes &&
                                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => handleOpenModalConfig(null)}>
                                    Adicionar
                                </Button>
                            }
                        </Box>

                        {
                            !existeConfiguracoes &&
                            <DisplayNotFoundInfo variant="caption" label="Nenhuma configuração encontrada" />
                        }

                        {
                            existeConfiguracoes &&
                            <Table aria-label="simple table">
                                <TableHead>
                                    <TableRow>
                                        <TableCell><b>Tipo</b></TableCell>
                                        <TableCell><b>Vigência</b></TableCell>
                                        <TableCell align="right"><b>Limite de crédito</b></TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {configsLimiteCredito.map((row: ConfigLimiteCred, index: number) => (
                                        <TableRow
                                            key={index}
                                            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                                        >
                                            <TableCell component="th" scope="row">
                                                {row.tipo}
                                            </TableCell>

                                            <TableCell>{`${row.vigencia.inicio?.format('DD/MM/YYYY')} até ${row.vigencia.fim?.format('DD/MM/YYYY')}`}</TableCell>

                                            <TableCell align="right">{row.limiteCredito}</TableCell>

                                            <TableCell>
                                                <IconButton
                                                    aria-label="edit"
                                                    size="small"
                                                    disabled={!(publico.situacao_financeira === "LIBERADO")}
                                                    onClick={() => handleOpenModalConfig(index)}
                                                >
                                                    <EditIcon fontSize="inherit" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        }
                    </>
                }

                {
                    modalConfig.state &&
                    <DialogConfiguracao
                        open={modalConfig.state}
                        index={modalConfig.index}
                        limite_alcada={modalConfig.limite_alcada}
                        handleClose={handleCloseModalConfig}
                    />
                }
            </DialogContent>

            <DialogActions>
                <Button
                    variant="outlined"
                    onClick={handleBack}
                >
                    Voltar
                </Button>
            </DialogActions>
        </>
    )
}

interface FormularioProps {
    handleClose: () => void
    handleBack: () => void
}

interface IModalConfig {
    state: boolean,
    index: number | null
    limite_alcada: number
}